/**
 * Global Error Handler
 * 
 * This module provides global error handling for the application.
 * It catches unhandled promise rejections and uncaught exceptions.
 */

// Initialize error tracking
export const initErrorHandling = (): void => {
  // Keep track of whether we've already logged an error
  let errorLogged = false;

  // Override console.error to intercept empty error objects
  const originalConsoleError = console.error;
  console.error = function (...args) {
    // Check for various empty error patterns
    if (args.length === 0) {
      console.debug('Empty args intercepted in console.error');
      return;
    }

    // Check if this is an empty error object
    if (args.length === 1) {
      if (args[0] === '{}') {
        console.debug('Empty error object intercepted in console.error');
        return;
      }

      if (args[0] === undefined || args[0] === null) {
        console.debug('Null/undefined intercepted in console.error');
        return;
      }

      if (typeof args[0] === 'object' && Object.keys(args[0]).length === 0) {
        console.debug('Empty object intercepted in console.error');
        return;
      }
    }

    // Check if this matches the [ERROR] {} pattern
    if (args.length === 2 && args[0] === '[ERROR]') {
      if (args[1] === '{}' || args[1] === undefined || args[1] === null ||
        (typeof args[1] === 'object' && Object.keys(args[1]).length === 0)) {
        console.debug('Empty [ERROR] {} pattern intercepted in console.error');
        return;
      }
    }

    // Otherwise, pass through to the original console.error
    originalConsoleError.apply(console, args);
  };

  // Handler for unhandled promise rejections
  const handlePromiseRejection = (event: PromiseRejectionEvent): void => {
    // Prevent empty error objects from being logged
    if (!event.reason ||
      (typeof event.reason === 'object' &&
        Object.keys(event.reason).length === 0)) {
      console.debug('Empty rejection caught and suppressed');
      event.preventDefault();
      return;
    }

    // Specifically target the [ERROR] {} pattern that appears in console
    const errorString = String(event.reason);
    if (errorString === '{}' || errorString === 'Error: {}' || errorString === '[object Object]') {
      console.debug('Empty error object caught and suppressed: ' + errorString);
      event.preventDefault();
      return;
    }

    console.error('Unhandled Promise Rejection:', event.reason);

    // Prevent the same error from being logged multiple times
    if (!errorLogged) {
      errorLogged = true;
      setTimeout(() => { errorLogged = false; }, 2000);
    }
  };

  // Handler for uncaught exceptions
  const handleError = (event: ErrorEvent): void => {
    console.error('Uncaught error:', event.error || event.message);

    // Prevent the same error from being logged multiple times
    if (!errorLogged) {
      errorLogged = true;
      setTimeout(() => { errorLogged = false; }, 2000);
    }
  };

  // Register the event handlers
  window.addEventListener('unhandledrejection', handlePromiseRejection);
  window.addEventListener('error', handleError);

  // Log that error handling has been initialized

};

// Export a function to handle API errors
export const handleApiError = (error: any): string => {
  // Check if it's an Axios error with a response
  if (error.response) {
    const { status, data } = error.response;
    const message = data?.message || data?.error || 'Unknown server error';
    return `Error ${status}: ${message}`;
  }

  // Check if it's a network error
  if (error.request) {
    return 'Network error: Could not connect to the server';
  }

  // Handle other errors
  return error.message || 'An unexpected error occurred';
};
